import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  Stack,
  TextField,
  Tooltip,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useCanSendTestEvent } from '@/services/permissions';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';
import { EventDataEditor } from './event-data-editor';
import { RecipientPicker, type SendEventRecipient } from './recipient-picker';
import { useSendEventForm } from './use-send-event-form';

/** The footer's Send button submits the fields in the body. */
const SEND_EVENT_FORM_ID = 'send-event-form';

/**
 * Send one event for one profile.
 *
 * Every surface that offers this opens the same modal: the events browser,
 * an event's own page, and the activity feed's Resend. What differs between
 * them is only how much is known up front — a profile, a name, a payload —
 * so all three are data the caller passes in rather than modes the modal
 * knows about.
 */

export type SendEventModalData = {
  /**
   * The workspace the event is sent in. Passed in rather than read here: a
   * managed modal mounts outside the app's own tree, so it must not depend
   * on where that tree happens to be.
   */
  workspaceId: number;
  /** The profile to send for, when the caller already knows it. */
  recipient?: SendEventRecipient | null;
  eventName?: string;
  /** Pretty-printed JSON of the event attributes. */
  eventData?: string;
  /** Where the modal was opened from. Recorded on every analytics event. */
  source: string;
  /** The viewer may not send: the form is inert and Send says why. */
  isReadOnly?: boolean;
  /** Called once the backend has had time to record the event. */
  onSent?: () => void;
};

/**
 * Whether this send retries one that was already recorded: the caller knows
 * both the profile and the event name. Analytics splits the two so "send an
 * event" is not read as retrying one.
 */
export function isResend(data: Pick<SendEventModalData, 'eventName' | 'recipient'>): boolean {
  return !isEmptyString(data.eventName ?? '') && data.recipient != null;
}

export function SendEventModal({ data, modalState, onClose }: PlumaManagedModalProps<SendEventModalData>) {
  const taxonomy = useProductTaxonomy();
  const canSend = useCanSendTestEvent();

  const terms = {
    singular: taxonomy.term('people.singular'),
    titleSingular: taxonomy.term('people.titleSingular'),
    titlePlural: taxonomy.term('people.titlePlural'),
  };
  const isDisabled = (data.isReadOnly ?? false) || !canSend;

  const form = useSendEventForm({
    workspaceId: data.workspaceId,
    source: data.source,
    isResend: isResend(data),
    isDisabled,
    initial: { recipient: data.recipient ?? null, name: data.eventName ?? '', data: data.eventData ?? '' },
    personSingular: terms.singular,
    onSent: data.onSent,
    onClose,
  });

  return (
    <Modal state={modalState} title="Send an event" onClose={onClose}>
      <ModalBody>
        <Stack gap="250">
          <RecipientPicker
            workspaceId={data.workspaceId}
            recipient={form.recipient}
            onChange={form.setRecipient}
            terms={terms}
            isDisabled={isDisabled}
          />

          {/* The recipient search is outside the form: Enter there runs the
              search, and it is the field's own control that hears it. */}
          <Form id={SEND_EVENT_FORM_ID} onSubmit={() => form.submit()}>
            <Stack gap="250">
              <TextField
                label="Event name"
                placeholder="e.g. account_update, cart_checkout, cart_abandon"
                value={form.name}
                error={form.nameError}
                isDisabled={isDisabled}
                onInput={(event) => form.setName(event.target.value)}
                onBlur={form.checkName}
              />

              <EventDataEditor
                data={form.data}
                onChange={form.setData}
                editorType={form.editorType}
                onEditorTypeChange={form.setEditorType}
                error={form.dataError}
                isDisabled={isDisabled}
              />
            </Stack>
          </Form>
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" isDisabled={form.isSending} onClick={() => onClose()}>
          Cancel
        </Button>
        {/* Disabled while something is missing, which the form rule allows
            where the flip is what says a field is required: the tooltip beside
            it names what is missing, and the page this replaces disables the
            same button for the same reasons. */}
        <Tooltip isDisabled={form.blockedReason == null} content={form.blockedReason ?? ''} isCustom={true}>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              type="submit"
              form={SEND_EVENT_FORM_ID}
              variant="primary"
              isDisabled={form.blockedReason != null}
              isLoading={form.isSending}
            >
              Send event
            </Button>
          )}
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
}
